import { css } from '@emotion/react'
import {
  cssVar,
  FontWeight,
  gemVar,
  Size,
  Spacing,
  ThemedColors,
  Typography,
  ZIndex,
} from '@opallabs/gem'
import { modernWithFallback } from '@opallabs/lambda'

export const drawer = css({
  color: gemVar(ThemedColors.TextPrimary),
  flex: '1 1 auto',
  flexDirection: 'row',
  zIndex: ZIndex.Shroud.Content + ZIndex.Drawer.Content,
  // A global rule paints selected text white, and the prosemirror rules the
  // composer picks up paint the highlight behind it transparent — together they
  // can leave selected text in here invisible. The cause is in those two sets of
  // styles, not this drawer; this only makes sure a reader can see what they
  // selected while it stands.
  '&::selection, & *::selection': {
    backgroundColor: gemVar(ThemedColors.Primary3),
    color: gemVar(ThemedColors.TextPrimary),
  },
})

/**
 * The preview column's width, set on the column itself — the single-version
 * section, or a compare pane — and read by everything stacked in it (canvas,
 * byline, details, feedback) so they stay flush. Sectioned
 * content renders Periodic's expanded layout, a fixed `EXPANDED_POST_INLINE_SIZE`
 * wide: the wider column lets it show at 1:1 instead of scaled down.
 */
const COLUMN_INLINE_SIZE_VAR = '--content-version-column-inline-size'
const columnInlineSize = `var(${COLUMN_INLINE_SIZE_VAR})`
const STANDARD_COLUMN_INLINE_SIZE = 680
/** `getBaseStyles(isExpanded: true)` in every Periodic section content type. */
const EXPANDED_POST_INLINE_SIZE = 900
/**
 * The canvas insets, kept tight around the expanded layout: it is a wide post in
 * a bounded drawer, and every pixel spent on padding is a pixel of content that
 * has to scale down. The column below is sized from the inline inset so the
 * canvas's content box lands on the post's own width and it previews at 1:1.
 */
const EXPANDED_CANVAS_PADDING = Spacing.M
const STANDARD_CANVAS_PADDING = Spacing.XL
/** Keeps the floating actions off the top of the post, however tight the insets. */
const CANVAS_ACTIONS_GUTTER = Spacing.XL

export const preview = (expanded: boolean) =>
  css({
    [COLUMN_INLINE_SIZE_VAR]: `${
      expanded
        ? EXPANDED_POST_INLINE_SIZE + EXPANDED_CANVAS_PADDING * 2
        : STANDARD_COLUMN_INLINE_SIZE
    }px`,
    inlineSize: '100%',
    marginBlock: '0',
    marginInline: 'auto',
    paddingBlock: '80px',
    paddingInline: Spacing.L,
    ...modernWithFallback(
      [{ overflowBlock: 'auto' }, { overflowY: 'auto' }],
      'overflow-block: auto'
    ),
    display: 'flex',
    flexFlow: 'column nowrap',
    alignItems: 'center',
    color: cssVar(ThemedColors.TextPrimary),
  })

export const previewHeader = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: Spacing.M,
  inlineSize: '100%',
  maxInlineSize: columnInlineSize,
  marginBlockEnd: Spacing.L,
})

export const previewHeaderTitle = css({
  ...Typography.H400,
  marginBlock: 0,
})

export const previewHeaderDate = css({
  ...Typography.T500,
  color: gemVar(ThemedColors.TextSecondary),
})

export const previewHeaderSubtitle = css({
  ...Typography.T500,
  color: gemVar(ThemedColors.TextSecondary),
})

export const steppingControls = css({
  display: 'flex',
  gap: Spacing.XS,
  alignItems: 'center',
})

export const previewBody = (expanded: boolean) =>
  css({
    position: 'relative',
    inlineSize: '100%',
    maxInlineSize: columnInlineSize,
    display: 'flex',
    justifyContent: 'center',
    paddingBlock: expanded
      ? `${CANVAS_ACTIONS_GUTTER}px ${EXPANDED_CANVAS_PADDING}px`
      : STANDARD_CANVAS_PADDING,
    paddingInline: expanded ? EXPANDED_CANVAS_PADDING : STANDARD_CANVAS_PADDING,
    borderRadius: Spacing.S,
    border: `1px solid ${gemVar(ThemedColors.StrokeSecondary1)}`,
    backgroundColor: gemVar(ThemedColors.Surface3),
    minBlockSize: 320,
    alignItems: 'flex-start',
    // The preview section is a height-bounded flex column; without this the
    // canvas compresses instead of scrolling and content overlaps the table.
    flexShrink: 0,
  })

// Floats over the canvas corner; the canvas is the positioning context.
export const previewActions = css({
  position: 'absolute',
  insetBlockStart: Spacing.S,
  insetInlineEnd: Spacing.S,
  display: 'flex',
  gap: Spacing.XS,
  // Above the post's own absolutely-positioned layers and the pin overlay.
  zIndex: 10002,
})

export const periodicFrame = css({
  position: 'relative',
  maxInlineSize: '100%',
  marginInline: 'auto',
})

/**
 * Lets the post lay out at its own intrinsic width instead of being squeezed into
 * the canvas. Periodic sizes each post from fixed pixel widths (900 expanded, 484
 * collapsed) that overflow a narrower parent silently, while the ResizeObserver
 * behind `onDisplayChange` reports the frame's *content box*. Squeezed, the frame
 * therefore under-reports: the post spills out of the canvas, the pin overlay
 * (inset 0) stops short of the content it covers, and the fit-to-width scale is
 * computed from the wrong width. Paired with `scaleClip`, which keeps the
 * unscaled-but-honest frame from escaping the canvas.
 */
export const intrinsicFrame = css({
  inlineSize: 'max-content',
  maxInlineSize: 'none',
})

/**
 * Centres the card inside a compare pane, whose frame spans the pane so it is
 * never sized from its own content. Periodic's cards carry widths of their own
 * (375 for a phone frame), so without this they sit against the pane's left edge
 * with all the slack on the right.
 *
 * `safe` matters: a card wider than the pane (the 484 collapsed carousel, when
 * the window is narrow enough that even the scale below cannot save it) would be
 * centred *out of* both edges, trading one clipped side for two.
 */
export const fluidFrame = css({
  display: 'flex',
  justifyContent: 'safe center',
})

export const scaleClip = css({
  inlineSize: '100%',
  overflow: 'hidden',
})

/**
 * Tightens the vertical rhythm of Periodic's expanded section grid so more of a
 * long post fits on screen. The grid is `ExpandedCarouselBody`, shared with the
 * composer's editing view — where the roomier spacing is correct, and where drag
 * targets depend on it — so this reaches into it by class from our canvas rather
 * than changing it there.
 *
 * Fragile by nature: `kuiper-expanded-asset-carousel` is the class that component
 * puts on the wrapper around its `ol`. Rename it, or nest the list deeper, and
 * this silently stops applying (the preview just gets roomier again).
 *
 * Pin coordinates are fractions of the frame this grid sizes, so these numbers
 * are part of the pin coordinate space: changing them moves every existing pin.
 * Safe while only dev pins exist; after launch a change here needs a new
 * `AnnotationLayout` key instead.
 */
export const expandedSectionSpacing = css({
  '.kuiper-expanded-asset-carousel > ol': {
    rowGap: Spacing.S,
    paddingBlockStart: Spacing.S,
    marginBlockEnd: 0,
  },
  // Each section stacks its own bottom margin on top of the row gap.
  '.kuiper-expanded-asset-carousel > ol > li': {
    marginBlockEnd: 0,
  },
})

export const compareGrid = css({
  inlineSize: '100%',
  display: 'grid',
  // Columns hug the canvas cap instead of splitting the full pane width, so on
  // wide windows the two panes sit centered next to each other rather than
  // drifting apart with dead space inside each column.
  gridTemplateColumns: `repeat(2, minmax(0, ${STANDARD_COLUMN_INLINE_SIZE}px))`,
  justifyContent: 'center',
  gap: Spacing.L,
  alignItems: 'start',
})

export const comparePane = css({
  // Each pane is its own column: the canvas, and the details table under it, cap
  // at the pane's width rather than at the width the single-version view set on
  // the section above (which would only agree with the grid by coincidence).
  [COLUMN_INLINE_SIZE_VAR]: `${STANDARD_COLUMN_INLINE_SIZE}px`,
  minInlineSize: 0,
  display: 'flex',
  flexFlow: 'column nowrap',
})

export const comparePaneHeader = css({
  marginBlockEnd: Spacing.M,
})

// The prompt shown in place of a preview: no pair picked to compare yet, or a
// deep link pointing at a version that is no longer there.
export const previewNotice = css({
  ...Typography.T500,
  color: gemVar(ThemedColors.TextSecondary),
  margin: 'auto',
})

export const fallbackCard = css({
  inlineSize: '100%',
  padding: Spacing.L,
  borderRadius: Spacing.S,
  border: `1px solid ${gemVar(ThemedColors.StrokeSecondary1)}`,
  backgroundColor: gemVar(ThemedColors.Surface1),
})

export const fallbackSnapshotFrame = css({
  position: 'relative',
  inlineSize: '100%',
})

export const fallbackSnapshot = css({
  // Block, not inline: an inline image leaves baseline space under it, and the
  // pin layer measures the frame, so those few pixels would shift every pin.
  display: 'block',
  inlineSize: '100%',
  blockSize: 'auto',
  borderRadius: Spacing.S,
  border: `1px solid ${gemVar(ThemedColors.StrokeSecondary1)}`,
})

export const fallbackTitle = css({
  ...Typography.H300,
  marginBlock: `0 ${Spacing.S}px`,
})

export const fallbackBody = css({
  ...Typography.T500,
  whiteSpace: 'pre-wrap',
  marginBlock: 0,
})

export const detailsTable = css({
  inlineSize: '100%',
  maxInlineSize: columnInlineSize,
  marginInline: 'auto',
  padding: Spacing.L,
  borderRadius: Spacing.S,
  border: `1px solid ${gemVar(ThemedColors.StrokeSecondary1)}`,
  backgroundColor: gemVar(ThemedColors.Surface1),
  flexShrink: 0,
})

export const detailsRow = css({
  display: 'flex',
  gap: Spacing.M,
  paddingBlock: Spacing.XS,
  ':not(:last-of-type)': {
    borderBlockEnd: `1px solid ${gemVar(ThemedColors.StrokeSecondary1)}`,
  },
})

export const detailsLabel = css({
  ...Typography.T300,
  color: gemVar(ThemedColors.TextSecondary),
  flex: '0 0 160px',
  margin: 0,
})

export const detailsValue = css({
  ...Typography.T300,
  margin: 0,
  overflowWrap: 'anywhere',
})

export const rail = css({
  inlineSize: '100%',
  maxInlineSize: 400,
  flexGrow: 0,
  flexShrink: 0,
  borderInlineStart: `1px solid ${gemVar(ThemedColors.StrokeSecondary1)}`,
  display: 'flex',
  flexFlow: 'column nowrap',
  ...Typography.T500,
})

export const railFilter = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: Spacing.M,
  paddingInline: Spacing.M,
  // Breathing room below the drawer header's tinted block above.
  paddingBlockStart: Spacing.M,
  paddingBlockEnd: Spacing.S,
})

export const railList = css({
  ...modernWithFallback(
    [{ overflowBlock: 'auto' }, { overflowY: 'auto' }],
    'overflow-block: auto'
  ),
  paddingInline: Spacing.S,
  listStyle: 'none',
  marginBlock: 0,
  paddingBlock: 0,
})

// The row itself: the lockup and the selected treatment, and the positioning
// context the trigger below stretches over.
export const rowItem = (selected: boolean) =>
  css({
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-start',
    gap: Spacing.S,
    padding: Spacing.S,
    marginBlockEnd: Spacing.XS,
    borderRadius: Spacing.S,
    border: `1px solid ${selected ? gemVar(ThemedColors.Primary6) : 'transparent'}`,
    backgroundColor: selected ? gemVar(ThemedColors.Primary1) : 'transparent',
    ':hover': {
      backgroundColor: selected
        ? gemVar(ThemedColors.Primary1)
        : gemVar(ThemedColors.Surface2),
    },
    ':hover button[aria-label="Delete version"], :focus-within button[aria-label="Delete version"]':
      {
        opacity: 1,
      },
  })

/**
 * The row's trigger, stretched over the whole row. Being positioned, it paints
 * over the row's static content, so a click anywhere that isn't one of the row's
 * own controls activates the row — without the trigger having to contain them.
 */
export const rowTrigger = css({
  position: 'absolute',
  inset: 0,
  padding: 0,
  border: 'none',
  borderRadius: 'inherit',
  background: 'transparent',
  cursor: 'pointer',
})

/**
 * The checkbox only reports the row's state — the trigger above owns the click.
 * Lapidary positions the input and lifts it a layer, which puts it over the
 * trigger, so without this a press on the checkbox itself lands on a read-only
 * input and does nothing.
 */
export const rowCheckbox = css({
  pointerEvents: 'none',
})

export const rowDetails = css({
  display: 'flex',
  flexFlow: 'column nowrap',
  gap: '2px',
  minInlineSize: 0,
})

export const railEmpty = css({
  ...Typography.T500,
  color: gemVar(ThemedColors.TextSecondary),
  padding: Spacing.M,
})

// Line 1: name left, one right-aligned slot (Current pill or hover-revealed
// delete — never both, the newest row can't be deleted).
// The trash trails the name/pill cluster directly — parked at the row's far
// edge it reads as a hole on unlabeled rows.
export const rowHeader = css({
  display: 'flex',
  alignItems: 'center',
  gap: Spacing.XS,
})

export const rowName = css({
  ...Typography.T500,
  fontWeight: 600,
})

// Name + label pill + Current pill cluster on the left of line 1.
export const rowNameGroup = css({
  display: 'flex',
  alignItems: 'center',
  gap: Spacing.XS,
  minInlineSize: 0,
})

// Line 2: timestamp · label pill · comment count, one muted metadata line.
export const rowMeta = css({
  ...Typography.T300,
  color: gemVar(ThemedColors.TextSecondary),
  display: 'flex',
  alignItems: 'center',
  gap: Spacing.XS,
  marginBlockStart: '2px',
})

export const labelInput = css({
  ...Typography.T500,
  inlineSize: '100%',
  paddingBlock: Spacing.XS,
  paddingInline: Spacing.S,
  borderRadius: Spacing.XS,
  border: `1px solid ${gemVar(ThemedColors.StrokeSecondary1)}`,
  backgroundColor: gemVar(ThemedColors.Surface1),
  color: gemVar(ThemedColors.TextPrimary),
})

export const addLabelButton = css({
  ...Typography.T300,
  cursor: 'pointer',
  border: 'none',
  background: 'none',
  padding: 0,
  color: gemVar(ThemedColors.TextLinks),
})

export const deleteButton = css({
  // Above the row trigger it shares the row with, so its own clicks reach it.
  position: 'relative',
  cursor: 'pointer',
  border: 'none',
  background: 'none',
  padding: 0,
  color: gemVar(ThemedColors.TextSecondary),
  // Revealed by the row's :hover/:focus-within rule in rowItem.
  opacity: 0,
  ':focus-visible': {
    opacity: 1,
  },
})

export const rowAnnotationCount = css({
  ...Typography.T300,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2px',
  color: gemVar(ThemedColors.TextSecondary),
})

// The product's placemark blue, matching the live snapshot annotation pin.
const PLACEMARK_BLUE = 'rgb(0, 191, 255)'

// The post's own absolutely-positioned layers (carousel slides) reach z-index
// 10000 within this frame, so the layer sits above them. Armed (comment mode),
// it catches clicks across the whole frame; disarmed it is click-transparent —
// only the pins (which reset pointer-events) stay interactive.
export const annotationOverlay = (armed: boolean) =>
  css({
    position: 'absolute',
    inset: 0,
    zIndex: 10001,
    cursor: armed ? 'crosshair' : undefined,
    pointerEvents: armed ? 'auto' : 'none',
    '> *': {
      pointerEvents: 'auto',
    },
  })

export const annotationPin = (highlighted: boolean, prominent: boolean) =>
  css({
    position: 'absolute',
    transform: `translate(-50%, -50%) scale(${highlighted ? 1.2 : 1})`,
    // Outside comment mode pins stay legible but let the content underneath
    // read through; hover/open (and comment mode) restore full opacity.
    opacity: highlighted || prominent ? 1 : 0.55,
    // A cursor over the content is click intent, so every pin becomes a plain
    // target while it is there. Hung on the frame rather than the pin layer
    // because that layer is click-transparent when disarmed, so its own `:hover`
    // never fires. `data-preview-content` is set on the frame in VersionPreview.
    '[data-preview-content]:hover &': {
      opacity: 1,
    },
    inlineSize: 24,
    blockSize: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    border: '2px solid white',
    background: PLACEMARK_BLUE,
    boxShadow: highlighted
      ? `0 0 0 4px ${PLACEMARK_BLUE}40, 0px 4px 12px 0px rgba(0, 0, 0, 0.24)`
      : '0px 4px 12px 0px rgba(0, 0, 0, 0.24)',
    color: 'white',
    ...Typography.T300,
    fontWeight: 600,
    cursor: 'pointer',
    transition:
      'transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease',
  })

export const annotationDraftPin = css({
  position: 'absolute',
  transform: 'translate(-50%, -50%)',
  inlineSize: 24,
  blockSize: 24,
  borderRadius: '50%',
  border: `2px dashed ${PLACEMARK_BLUE}`,
  background: `${PLACEMARK_BLUE}33`,
  boxSizing: 'border-box',
})

// Small label above the Feedback and Content Details containers.
export const sectionHeading = css({
  ...Typography.H50,
  inlineSize: '100%',
  maxInlineSize: columnInlineSize,
  marginBlock: `${Spacing.L}px ${Spacing.XS}px`,
  marginInline: 'auto',
  color: gemVar(ThemedColors.TextPrimary),
})

export const feedbackCard = css({
  inlineSize: '100%',
  maxInlineSize: columnInlineSize,
  marginInline: 'auto',
  padding: Spacing.S,
  borderRadius: Spacing.S,
  border: `1px solid ${gemVar(ThemedColors.StrokeSecondary1)}`,
  backgroundColor: gemVar(ThemedColors.Surface1),
  flexShrink: 0,
})

export const feedbackEmpty = css({
  ...Typography.T500,
  color: gemVar(ThemedColors.TextSecondary),
  margin: 0,
})

export const feedbackList = css({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexFlow: 'column nowrap',
  gap: Spacing.XS,
})

export const feedbackRow = (highlighted: boolean) =>
  css({
    display: 'flex',
    gap: Spacing.S,
    inlineSize: '100%',
    padding: Spacing.S,
    borderRadius: Spacing.XS,
    border: 'none',
    textAlign: 'start',
    cursor: 'pointer',
    color: 'inherit',
    backgroundColor: highlighted
      ? gemVar(ThemedColors.Surface2)
      : 'transparent',
  })

export const feedbackBadge = css({
  flex: '0 0 auto',
  inlineSize: 20,
  blockSize: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  border: '2px solid white',
  background: PLACEMARK_BLUE,
  color: 'white',
  ...Typography.T300,
  fontWeight: 600,
})

export const feedbackRowBody = css({
  display: 'flex',
  flexFlow: 'column nowrap',
  gap: '2px',
  minInlineSize: 0,
})

export const feedbackComment = css({
  ...Typography.T500,
  margin: 0,
  overflowWrap: 'anywhere',
})

export const feedbackMeta = css({
  ...Typography.T300,
  color: gemVar(ThemedColors.TextSecondary),
})

/**
 * A mentioned name, in the composer and in every view that reads a comment back.
 * The treatment a mention gets in a document comment (`Thread/Comment/styles`),
 * in theme-aware tokens rather than that surface's hardcoded light ones.
 */
const mentionAppearance = {
  backgroundColor: gemVar(ThemedColors.Primary3),
  borderRadius: Spacing.XS,
  color: gemVar(ThemedColors.TextLinks),
  padding: `${Spacing.XS / 2}px ${Spacing.XS}px`,
}

export const mentionName = css(mentionAppearance)

// Sits under the open feedback, separated by a rule when both are present.
export const resolvedGroup = (divided: boolean) =>
  css({
    display: 'flex',
    flexFlow: 'column nowrap',
    gap: Spacing.XS,
    ...(divided && {
      marginBlockStart: Spacing.XS,
      paddingBlockStart: Spacing.XS,
      borderBlockStart: `1px solid ${gemVar(ThemedColors.StrokeSecondary1)}`,
    }),
  })

export const resolvedToggle = css({
  ...Typography.T500,
  display: 'flex',
  alignItems: 'center',
  gap: Spacing.XS,
  padding: Spacing.XS,
  border: 'none',
  borderRadius: Spacing.XS,
  background: 'transparent',
  cursor: 'pointer',
  textAlign: 'start',
  color: gemVar(ThemedColors.TextSecondary),
})

// Resolved feedback has no pin to select, so the row is static markup with the
// unresolve affordance in its trailing slot.
export const resolvedRow = (highlighted: boolean) =>
  css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: Spacing.S,
    padding: Spacing.S,
    borderRadius: Spacing.XS,
    backgroundColor: highlighted
      ? gemVar(ThemedColors.Surface2)
      : 'transparent',
  })

/**
 * The composer's bordered box, mirroring a document comment's
 * `textAndActionsContainer`. Deliberately no `inlineSize`: it sizes to the
 * popover's content box, where a bordered, padded 100%-wide box overflowed the
 * card's rounded edges.
 */
export const composerFrame = css({
  borderRadius: Size.Rounding * 3,
  border: `1px solid ${gemVar(ThemedColors.StrokeSecondary1)}`,
  backgroundColor: gemVar(ThemedColors.Surface2),
  paddingInline: Spacing.S,
  paddingBlock: Spacing.S,

  '&:focus-within': {
    borderColor: gemVar(ThemedColors.StrokePrimary2),
    backgroundColor: gemVar(ThemedColors.Surface1),
  },
})

/** Mirrors a document comment editor's content styles. */
export const composerContent = css({
  ...Typography.T500,
  color: gemVar(ThemedColors.TextPrimary),
  textAlign: 'start',
  maxBlockSize: 95,
  ...modernWithFallback(
    [{ overflowBlock: 'auto' }, { overflowY: 'auto' }],
    'overflow-block: auto'
  ),

  '.tiptap': { outline: 'none' },
  '.tiptap > :first-child': { marginBlockStart: 0 },
  '.tiptap p': { marginBlock: '4px', marginInline: 0 },
  // How the Placeholder extension marks the empty document.
  '.tiptap p.is-editor-empty:first-of-type::before': {
    content: 'attr(data-placeholder)',
    color: gemVar(ThemedColors.TextSecondary),
    float: 'inline-start',
    blockSize: 0,
    pointerEvents: 'none',
  },
  '[data-type="mention"]': mentionAppearance,
})

export const composerActions = css({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: Spacing.S,
})

export const byline = css({
  inlineSize: '100%',
  maxInlineSize: columnInlineSize,
  marginBlock: `${Spacing.M}px 0`,
  marginInline: 'auto',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: Spacing.M,
  flexShrink: 0,
})

export const bylineAuthor = css({
  display: 'flex',
  alignItems: 'center',
  gap: Spacing.S,
  minInlineSize: 0,
})

// Author name over the timestamp, mirroring the tiptap comment header.
export const bylineText = css({
  display: 'flex',
  flexFlow: 'column nowrap',
  minInlineSize: 0,
})

export const bylineName = css({
  ...Typography.T500,
  fontWeight: FontWeight.Semibold,
  color: gemVar(ThemedColors.TextPrimary),
})

export const bylineDate = css({
  ...Typography.T300,
  color: gemVar(ThemedColors.TextSecondary),
})

export const bylineLabel = css({
  display: 'flex',
  alignItems: 'center',
  gap: Spacing.S,
  flexShrink: 0,
})

// Empty-label affordance: a bordered pill inviting the user to name the version.
export const addLabelChip = css({
  ...Typography.T300,
  display: 'inline-flex',
  alignItems: 'center',
  gap: Spacing.XS,
  paddingBlock: Spacing.XS,
  paddingInline: Spacing.S,
  borderRadius: 999,
  border: `1px solid ${gemVar(ThemedColors.StrokeSecondary1)}`,
  background: 'none',
  cursor: 'pointer',
  color: gemVar(ThemedColors.TextSecondary),
})

// Portaled popover content sits above the full-takeover drawer (which itself
// stacks above the shroud), so it needs a matching drawer-relative z-index.
export const popoverZIndex = ZIndex.Shroud.Content + ZIndex.Drawer.Popover

/**
 * The delete confirmation is a Lapidary Modal rendered inside the drawer, and
 * the Modal's own stacking puts it at the shroud's level — behind a drawer that
 * sits above the shroud. Both halves have to be lifted over the drawer panel:
 * the dimming overlay so it covers the drawer, the dialog so it sits on top.
 */
export const deleteModalOverlay = {
  zIndex: ZIndex.Shroud.Content + ZIndex.Drawer.Footer,
}
export const deleteModal = {
  zIndex: ZIndex.Shroud.Content + ZIndex.Drawer.Popover,
}

/**
 * Anything that has to sit above an open pin popover — the mention suggestion
 * list, and tooltips on the popover's own actions. The list carries its own
 * `ZIndex.Shroud.Popover` from the document-comment surface, which is *below*
 * `popoverZIndex`; the wrapper this is set on gives it a stacking context above.
 */
export const abovePopoverZIndex = ZIndex.Shroud.Content + ZIndex.Drawer.Tooltip

// Mirrors the tiptap document-comment card: an avatar/name/time header over the
// comment body, with the body indented under the name.
export const pinPopover = css({
  padding: Spacing.M,
  maxInlineSize: 280,
  display: 'flex',
  flexFlow: 'column nowrap',
  gap: Spacing.XS,
})

export const pinPopoverHeader = css({
  display: 'flex',
  alignItems: 'center',
  gap: Spacing.S,
})

export const pinPopoverName = css({
  ...Typography.T500,
  fontWeight: FontWeight.Bold,
  // The popover is a fixed 280 wide, so a long name wraps rather than pushing
  // the timestamp and resolve action out of it. `anywhere` covers names with
  // no break points at all.
  minInlineSize: 0,
  overflowWrap: 'anywhere',
  color: gemVar(ThemedColors.TextPrimary),
})

export const pinPopoverTime = css({
  ...Typography.T500,
  lineHeight: `${Spacing.M}px`,
  // Short ("May 10", "3:42 PM") — keep it whole and let the name wrap instead.
  whiteSpace: 'nowrap',
  flexShrink: 0,
  color: gemVar(ThemedColors.TextSecondary),
})

/** Trailing action in the header row, where a document comment keeps its own. */
export const pinPopoverAction = css({
  marginInlineStart: 'auto',
  color: gemVar(ThemedColors.TextSecondary),
})

export const pinPopoverComment = css({
  ...Typography.T500,
  fontSize: 16,
  margin: 0,
  paddingInlineStart: Spacing.L + Spacing.S,
  color: gemVar(ThemedColors.TextPrimary),
  overflowWrap: 'anywhere',
})

export const draftPopover = css({
  padding: Spacing.M,
  inlineSize: 260,
  display: 'flex',
  flexFlow: 'column nowrap',
  gap: Spacing.S,
})
